/*
 * assets/card-carousel.js : the <card-carousel> custom element, the ONE behaviour behind every card carousel in
 * the theme (section audit batch 2, 2026-09-11 ; owner decision D10, review-brief ruling 3).
 *
 * WHY ONE ASSET : five sections carried five inline carousel scripts (three of ours mirroring each other line for
 * line, two the Sections Pro vendor's) and the home page showed one behaviour in five drifting copies. The owner
 * ruled the behaviour once, and this file is that ruling in code :
 *   - a fixed card width on desktop, the visible group centred, every further card waiting off to the right
 *     behind the arrows (owner, 2026-09-09) ;
 *   - real <button> arrows that stay in place at either end and go INERT rather than hidden, so a click there
 *     lands on the arrow and never on the card beneath (owner, 2026-09-10) ; both hidden only when nothing scrolls ;
 *   - a step of one card, read at click time so a resize is honoured ; scroll-snap keeps the row aligned ;
 *   - a re-fit on resize ; the theme editor's selected block scrolled into view ;
 *   - optional autoplay that pauses while the visitor hovers, focuses or edits, stops for good once they take the
 *     controls, and never runs against a reduce-motion preference.
 * The SECTION supplies the markup and the CSS (its own class names, colours, bands and arrow icons) ; this file
 * supplies the behaviour through data-attribute hooks, so no section needs to know another's class names.
 *
 * ============================================================================================================
 * THE CONTRACT : the sections are written to this header ; nothing else in this file is public.
 * ============================================================================================================
 *
 * LOADING : `<script src="{{ 'card-carousel.js' | asset_url }}" defer="defer"></script>` in the section, the way
 * layout/theme.liquid loads the theme's own components. defer matters : the element reads its children when it
 * connects, so the file must run after the markup is parsed. A section placed twice emits the tag twice and the
 * browser runs the file twice : the guarded define below makes the second run a no-op.
 *
 * MARKUP :
 *
 *   <card-carousel data-card-width="350" data-gap="20" data-cards-per-row="4"
 *                  [data-autoplay="true" data-autoplay-interval="5000"]>
 *     [<div data-card-carousel-heading="cards">...</div>]        optional, see HEADING
 *     <div data-card-carousel-frame>
 *       <ul data-card-carousel-row>
 *         <li>...</li> <li>...</li> ...                            every element child of the row is a card
 *       </ul>
 *       <button type="button" data-card-carousel-prev aria-label="Scroll left" aria-disabled="true" [hidden]>...</button>
 *       <button type="button" data-card-carousel-next aria-label="Scroll right" [hidden]>...</button>
 *     </div>
 *   </card-carousel>
 *
 *   data-card-width         the desktop card width in px ("Card Width"). Absent or 0 : no desktop fit ; the CSS
 *                           bands rule at every width and the element only drives the arrows.
 *   data-gap                the gap between cards in px ("Grid Gap") : the same number the row's CSS gap uses.
 *   data-cards-per-row      the desktop cap ("Cards per Row").
 *   data-autoplay           "true" turns autoplay on ("Enable Autoplay") ; anything else leaves it off.
 *   data-autoplay-interval  milliseconds between steps ("Autoplay Time" : the Sections Pro select values are ms).
 *
 *   The element itself is the HOST : its content box is the width there is to fill, so it sits where the
 *   section's container padding and maximum width apply (inside .spro-container, inside .page-width, or as the
 *   padded block itself). The frame sits inside it and carries no horizontal padding of its own ; the arrows sit
 *   anywhere inside the element. Server-rendered arrow state, so nothing flashes before the script runs : both
 *   arrows `hidden` when the cards present are no more than data-cards-per-row, and the left one
 *   aria-disabled="true" ; the element corrects both as soon as it connects.
 *
 * WHAT THE ELEMENT WRITES, and nothing else :
 *   - on the row, inline, from 990px : `--card-carousel-basis: <width>px`, the card width in force (the fixed
 *     width, or the available width when even one card does not fit) ; removed below 990px ;
 *   - on the frame, inline, from 990px : `width`, exactly the visible cards (count * width + (count - 1) * gap,
 *     plus the row's own horizontal padding) ; removed below 990px ;
 *   - on the heading block, inline, the frame's width (see HEADING) ; removed below 990px ;
 *   - on the arrows : `hidden` (both, only when nothing scrolls) and aria-disabled="true" | "false".
 *
 * THE FIT RULE (desktop, from 990px, exactly the pricing grid's) :
 *   available = host content width - row horizontal padding
 *   width     = min(card_width, available)
 *   count     = clamp(floor((available + gap) / (width + gap)), 1, min(cards_per_row, cards present))
 *   frame     = count * width + (count - 1) * gap  (+ row padding), centred by the frame's auto margins
 * Below 990px the element removes its inline values and the section's CSS bands are the layout.
 *
 * CSS THE SECTION SUPPLIES (its own selectors ; the custom properties are the contract) :
 *
 *   card-carousel { display: block; }                 a custom element is inline until told otherwise, and an
 *                                                     inline box measures 0 wide : without this line the fit
 *                                                     rule sees no room and every card collapses
 *   [frame] { position: relative; margin: 0 auto; }   the auto margins centre the visible group
 *   [row]   { display: flex; gap: <gap>px; overflow-x: auto; scroll-snap-type: x mandatory;
 *             scrollbar-width: none;
 *             --card-carousel-gap: <gap>px;
 *             --card-carousel-cards: <cards_per_row>;
 *             --card-carousel-basis: calc((100% - (var(--card-carousel-cards) - 1) * var(--card-carousel-gap))
 *                                         / var(--card-carousel-cards)); }
 *   [row]::-webkit-scrollbar { display: none; }
 *   card    { flex: 0 0 var(--card-carousel-basis); scroll-snap-align: start; box-sizing: border-box; }
 *   @media (min-width: 990px)  { [row] { --card-carousel-basis: <card_width>px; } }
 *                                                     the first paint already matches, before the element measures
 *   @media (max-width: 989px)  { [row] { --card-carousel-cards: <the tablet count>; } }
 *   @media (max-width: 599px)  { [row] { --card-carousel-cards: <mobile_cards>; } }
 *                                                     the bands below 990px are the section's (decision D7 ; the
 *                                                     600px card-count sub-band is the owner's, 2026-09-09)
 *   arrows  : real <button type="button"> elements with an accessible name ;
 *             [hidden] { display: none; }             the arrow's own display rule outranks the UA hidden style
 *             [aria-disabled="true"] { opacity: 0.35; cursor: default; }   inert, still in place, still focusable
 *   heading : margin-left: auto; margin-right: auto;  when it takes the frame width (see HEADING)
 *
 * HEADING : a block marked data-card-carousel-heading="cards" ("Heading Position" aligned with the cards) is
 * given the frame's width on desktop, so with auto margins its left edge is the first card's. Any other value,
 * or no such block, is left alone : those positions need no script.
 *
 * AUTOPLAY (data-autoplay="true") : the row steps one card every data-autoplay-interval ms, forward to the end
 * and then back to the start (the vendor's bounce, kept) ; it runs only while the row is at least half on screen
 * (the vendor's start-when-visible rule, applied continuously) ; it PAUSES while the pointer is over the element,
 * while anything inside it has keyboard focus, and while a block is selected in the theme editor, and resumes
 * when that ends ; it STOPS FOR GOOD once the visitor takes the controls (a pointer or touch down on the row, a
 * click on either arrow) : advancing away from what they chose to look at is the annoyance a stop exists to
 * prevent ; it never runs under prefers-reduced-motion: reduce, and follows that preference live. A row with
 * nothing to scroll never ticks.
 *
 * EVENTS THE ELEMENT HANDLES : click on the arrows ; scroll on the row ; resize on window (one listener per
 * element) ; pointerdown on the row ; mouseenter, mouseleave, focusin and focusout on the element ;
 * shopify:block:select and shopify:block:deselect on the element (they bubble from the block) ; change on the
 * reduce-motion media query.
 *
 * EDITOR LIFECYCLE : connectedCallback initialises and disconnectedCallback tears down every listener, timer and
 * observer. The theme editor re-renders a section's markup on every setting change : the old element leaves the
 * document (torn down) and the new one enters (initialised), so no shopify:section:load or
 * shopify:section:unload listener is needed and none is registered. shopify:block:select scrolls the selected
 * block into view so a card past the visible count can be edited without hunting for it.
 *
 * NO GLOBALS beyond the element definition and no console output. Several carousels on one page, or the same
 * section placed twice, are independent : every piece of state lives on the element instance.
 */
(function () {
  // Each section placement emits the script tag, so this file runs once per placement : only the first run may
  // define the element (a second define throws NotSupportedError, so an unguarded second run would end in an
  // uncaught error).
  if (window.customElements.get('card-carousel')) return;

  // A smooth scroll can settle a few pixels short of an edge : within this slack the row counts as at the edge,
  // so the arrow there still goes inert (the pricing grid's value, kept for every carousel).
  const EDGE_SLACK = 25;

  // Desktop only, the same 990px line as the CSS bands : below it the phone and tablet bands are an owner ruling
  // (2026-09-09) and stand untouched.
  const DESKTOP = '(min-width: 990px)';

  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  // Autoplay runs only while at least this share of the row is on screen (the vendor's threshold, kept). It is
  // both the observer's threshold (when the callback fires) and the share the callback tests (see visibility) :
  // the two must agree, or the row would be judged at a mark the observer never reports.
  const VISIBLE_SHARE = 0.5;

  function horizontalPadding(element) {
    const style = window.getComputedStyle(element);
    return (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
  }

  // Inert is aria-disabled, not the disabled property : a disabled button drops the keyboard focus that reached
  // it, and these are buttons precisely so the keyboard can reach them.
  function setInert(arrow, inert) {
    arrow.setAttribute('aria-disabled', inert ? 'true' : 'false');
  }

  function isInert(arrow) {
    return arrow.getAttribute('aria-disabled') === 'true';
  }

  class CardCarousel extends HTMLElement {
    constructor() {
      super();
      // Bound once, here, so disconnectedCallback removes the very functions connectedCallback added.
      this.onResize = this.layout.bind(this);
      this.onScroll = this.setArrows.bind(this);
      this.onPreviousClick = this.stepBack.bind(this);
      this.onNextClick = this.stepForward.bind(this);
      this.onBlockSelect = this.selectBlock.bind(this);
      this.onBlockDeselect = this.deselectBlock.bind(this);
      this.onPointerDown = this.takeControls.bind(this);
      this.onMouseEnter = this.pointerIn.bind(this);
      this.onMouseLeave = this.pointerOut.bind(this);
      this.onFocusIn = this.focusIn.bind(this);
      this.onFocusOut = this.focusOut.bind(this);
      this.onVisibility = this.visibility.bind(this);
      this.onMotionChange = this.syncAutoplay.bind(this);
      this.onTick = this.tick.bind(this);
      this.initialised = false;
    }

    connectedCallback() {
      this.row = this.querySelector('[data-card-carousel-row]');
      this.frame = this.querySelector('[data-card-carousel-frame]');
      this.previous = this.querySelector('[data-card-carousel-prev]');
      this.next = this.querySelector('[data-card-carousel-next]');
      // A missing hook is an authoring error in the section, visible in the editor as a row that does not move ;
      // the element does nothing rather than throw into the page.
      if (!this.row || !this.frame || !this.previous || !this.next) return;

      this.heading = this.querySelector('[data-card-carousel-heading="cards"]');
      this.gap = parseInt(this.dataset.gap, 10) || 0;
      this.cardWidth = parseInt(this.dataset.cardWidth, 10) || 0;
      this.maxCards = parseInt(this.dataset.cardsPerRow, 10) || 1;

      this.previous.addEventListener('click', this.onPreviousClick);
      this.next.addEventListener('click', this.onNextClick);
      this.row.addEventListener('scroll', this.onScroll);
      window.addEventListener('resize', this.onResize);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.setupAutoplay();
      this.initialised = true;
      this.layout();
    }

    disconnectedCallback() {
      if (!this.initialised) return;
      this.initialised = false;
      this.previous.removeEventListener('click', this.onPreviousClick);
      this.next.removeEventListener('click', this.onNextClick);
      this.row.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (!this.autoplayEnabled) return;
      clearInterval(this.timer);
      this.timer = 0;
      this.observer.disconnect();
      this.reducedMotion.removeEventListener('change', this.onMotionChange);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.row.removeEventListener('pointerdown', this.onPointerDown);
    }

    // Fixed-width cards on desktop : how many whole cards fit is the width available (the host less its padding
    // and the row's own) plus one gap, over a card plus its gap, floored, capped by "Cards per Row" and by the
    // cards actually in the row (a cap above the cards present would size the frame for cards that are not there
    // and push the group off centre), and never below one. The frame is then sized to exactly those cards, so
    // the next card is clipped rather than peeking, and its auto margins centre it. Off desktop every inline
    // value is removed so the section's bands rule.
    fitCards() {
      if (!this.cardWidth || !window.matchMedia(DESKTOP).matches) {
        this.frame.style.removeProperty('width');
        this.row.style.removeProperty('--card-carousel-basis');
        if (this.heading) this.heading.style.removeProperty('width');
        return;
      }
      const rowPadding = horizontalPadding(this.row);
      const available = this.clientWidth - horizontalPadding(this) - rowPadding;
      const width = Math.min(this.cardWidth, available);
      const present = this.row.children.length;
      const count = Math.max(1, Math.min(this.maxCards, present, Math.floor((available + this.gap) / (width + this.gap))));
      this.row.style.setProperty('--card-carousel-basis', width + 'px');
      this.frame.style.width = (count * width + (count - 1) * this.gap + rowPadding) + 'px';
      // "Aligned with the cards" : the heading block takes the frame's width, so its left edge is the first card's.
      if (this.heading) this.heading.style.width = this.frame.style.width;
    }

    // One card plus the gap after it : the row moves card by card and the snap points keep it aligned. Read at
    // click time so a resize is honoured.
    step() {
      const first = this.row.firstElementChild;
      return first ? first.clientWidth + this.gap : 0;
    }

    // Nothing to scroll : no arrows. Otherwise both stay in place, and the one at its end is made inert rather
    // than removed, so a click there lands on the arrow and not on the card beneath (owner, 2026-09-10).
    setArrows() {
      const row = this.row;
      const scrollable = row.scrollWidth > row.clientWidth + EDGE_SLACK;
      this.previous.hidden = !scrollable;
      this.next.hidden = !scrollable;
      setInert(this.previous, row.scrollLeft <= EDGE_SLACK);
      setInert(this.next, row.scrollLeft + row.clientWidth + EDGE_SLACK >= row.scrollWidth);
    }

    // The scroll listener updates the arrows as the row moves.
    scrollRow(delta) {
      this.row.scroll({ left: this.row.scrollLeft + delta, behavior: 'smooth' });
    }

    layout() {
      this.fitCards();
      this.setArrows();
    }

    // An arrow click is the visitor taking the controls (autoplay stops for good), then the step, unless the
    // arrow is inert : an inert arrow swallows its click, that being the point of leaving it in place.
    stepBack() {
      this.takeControls();
      if (!isInert(this.previous)) this.scrollRow(-this.step());
    }

    stepForward() {
      this.takeControls();
      if (!isInert(this.next)) this.scrollRow(this.step());
    }

    // Theme editor : selecting a block in the sidebar brings it into view, so a card past the visible count can
    // be edited without hunting for it ; autoplay holds still while it is selected, so the merchant's view stays
    // where they put it.
    selectBlock(event) {
      event.target.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
      this.selected = true;
      this.syncAutoplay();
    }

    deselectBlock() {
      this.selected = false;
      this.syncAutoplay();
    }

    // ---- autoplay -------------------------------------------------------------------------------------------

    setupAutoplay() {
      this.autoplayInterval = parseInt(this.dataset.autoplayInterval, 10) || 0;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.autoplayInterval > 0;
      this.timer = 0;
      if (!this.autoplayEnabled) return;
      this.stopped = false;
      this.hovered = false;
      this.focused = false;
      this.selected = false;
      // Off screen until the observer reports otherwise, so a carousel below the fold never scrolls unseen.
      this.visible = false;
      this.direction = 1;
      this.reducedMotion = window.matchMedia(REDUCED_MOTION);
      this.reducedMotion.addEventListener('change', this.onMotionChange);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.row.addEventListener('pointerdown', this.onPointerDown);
      this.observer = new IntersectionObserver(this.onVisibility, { threshold: VISIBLE_SHARE });
      this.observer.observe(this.row);
    }

    // Every pause and stop reason funnels through here : the timer exists exactly when all of them are clear.
    // A resume starts a fresh interval, so the first step after a pause comes a full interval later.
    syncAutoplay() {
      const play = this.autoplayEnabled && !this.stopped && this.visible && !this.hovered && !this.focused
        && !this.selected && !this.reducedMotion.matches;
      if (play === Boolean(this.timer)) return;
      if (play) {
        this.timer = setInterval(this.onTick, this.autoplayInterval);
      } else {
        clearInterval(this.timer);
        this.timer = 0;
      }
    }

    // Forward to the end, then back to the start : the arrow state already says which end the row is at.
    tick() {
      if (this.previous.hidden) return;
      if (this.direction > 0 && isInert(this.next)) {
        this.direction = -1;
      } else if (this.direction < 0 && isInert(this.previous)) {
        this.direction = 1;
      }
      this.scrollRow(this.direction * this.step());
    }

    takeControls() {
      this.stopped = true;
      this.syncAutoplay();
    }

    pointerIn() {
      this.hovered = true;
      this.syncAutoplay();
    }

    pointerOut() {
      this.hovered = false;
      this.syncAutoplay();
    }

    focusIn() {
      this.focused = true;
      this.syncAutoplay();
    }

    // focusout fires when focus moves between two children as well as when it leaves : only the latter resumes.
    focusOut(event) {
      this.focused = Boolean(event.relatedTarget && this.contains(event.relatedTarget));
      this.syncAutoplay();
    }

    // The share on screen is read from the ratio, never from isIntersecting : the observer reports once at
    // observe time, at every VISIBLE_SHARE crossing and at every change between on and off screen, and its
    // isIntersecting is true at any overlap at all (one pixel of the row counts). Only the ratio says which side
    // of the half mark the row is on.
    visibility(entries) {
      for (const entry of entries) {
        if (entry.target === this.row) this.visible = entry.intersectionRatio >= VISIBLE_SHARE;
      }
      this.syncAutoplay();
    }
  }

  window.customElements.define('card-carousel', CardCarousel);
})();
